import { PDFDownloadLink } from '@react-pdf/renderer';
import React, { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { Button } from 'antd';
import { getAccessTokenApi } from 'api/auth';
import { getImagesApi, getItemsApi, getStaticBudgetData } from 'api/budget';
import useTemplateOrFallback from 'PDF/TemplateEngine/useTemplateOrFallback';
import TemplateRenderer from 'PDF/TemplateEngine/TemplateRenderer';

// Budget V3 — pipeline 100% low-code.
// El PDF se arma directamente desde TemplateRenderer (sin BudgetDocument
// hardcoded). `/presupuestos/document-static-data` devuelve `data` aplanado
// (columnas tenant + filas tenant_texts presupuesto fusionadas) + colors +
// logos. El componente compone el `data` final:
//   1. Runtime del budget (id, reason, date, guarantee, moneda, etc.).
//   2. Runtime del cliente aplanado con prefix client_* (rut→client_rut,
//      contactName→client_contact, etc.).
//   3. staticData (tenant_texts + columnas tenant) que vino del backend.
//   4. items + images (arrays que consumen table/images sections).
//
// `guarantee_text` es la cláusula de garantía ya resuelta y congelada al
// crear el presupuesto (nula en presupuestos históricos). La plantilla por
// defecto decide qué sección de garantía imprimir vía `visibleIf`, que solo
// compara contra literales (no truthiness) — por eso el `data` manda
// `has_guarantee_clause` como booleano explícito.
export function buildBudgetData(budget, items, images, staticResponse) {
  const flat = staticResponse?.data || {};
  return {
    ...flat,
    id: budget?.idBudget,
    reason: budget?.reason,
    date: budget?.createdOn ? dayjs(budget.createdOn).format('DD/MM/YYYY') : '',
    description: budget?.description,
    conditions: budget?.conditions,
    finishTime: budget?.finishTime,
    include: budget?.include,
    guarantee: budget?.guarantee,
    guarantee_text: budget?.guarantee_text ?? '',
    has_guarantee_clause: Boolean(budget?.guarantee_text),
    discount: budget?.discount || 0,
    moneda: budget?.moneda || 'CLP',
    tipo_cambio_snapshot: budget?.tipo_cambio_snapshot,
    client_name: budget?.name,
    client_rut: budget?.rut,
    client_address: budget?.address,
    client_commune: budget?.commune,
    client_phone: budget?.phone,
    client_email: budget?.email,
    client_contact: budget?.contactName,
    items,
    images,
  };
}

function BudgetDowload(props) {
  const { budget } = props;
  const token = getAccessTokenApi();
  const [items, setItems] = useState([]);
  const [images, setImages] = useState([]);
  const [staticResponse, setStaticResponse] = useState(null);

  // Banderas de carga por fetch. NO alcanza con mirar el contenido: `items` e
  // `images` vacios son un valor final legitimo (un presupuesto sin fotos), asi
  // que hay que registrar aparte si la respuesta ya llego.
  const [itemsReady, setItemsReady] = useState(false);
  const [imagesReady, setImagesReady] = useState(false);
  const [staticReady, setStaticReady] = useState(false);

  const { template, loading: templateLoading } = useTemplateOrFallback('presupuesto');

  useEffect(() => {
    let cancelled = false;
    setItemsReady(false);
    getItemsApi(token, budget.idBudget)
      .then((result) => {
        if (!cancelled) setItems(result.items);
      })
      // `finally` y no `then`: si el fetch falla, el boton se habilita igual en
      // vez de quedar colgado para siempre.
      .catch((err) => {
        // Sin este catch la cadena termina en un unhandled rejection cuando el
        // fetch falla. El documento se arma sin este dato, pero el boton se
        // habilita igual (ver el `finally`) para no dejarlo colgado.
        console.error('[PDF] No se pudo cargar un dato del documento:', err);
      })
      .finally(() => {
        if (!cancelled) setItemsReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [token, budget?.idBudget]);
  useEffect(() => {
    let cancelled = false;
    setStaticReady(false);
    getStaticBudgetData(token)
      .then((result) => {
        if (!cancelled) setStaticResponse(result || {});
      })
      .catch((err) => {
        // Sin este catch la cadena termina en un unhandled rejection cuando el
        // fetch falla. El documento se arma sin este dato, pero el boton se
        // habilita igual (ver el `finally`) para no dejarlo colgado.
        console.error('[PDF] No se pudo cargar un dato del documento:', err);
      })
      .finally(() => {
        if (!cancelled) setStaticReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);
  useEffect(() => {
    let cancelled = false;
    setImagesReady(false);
    getImagesApi(token, budget?.idBudget)
      .then((result) => {
        if (!cancelled) setImages(result.images);
      })
      .catch((err) => {
        // Sin este catch la cadena termina en un unhandled rejection cuando el
        // fetch falla. El documento se arma sin este dato, pero el boton se
        // habilita igual (ver el `finally`) para no dejarlo colgado.
        console.error('[PDF] No se pudo cargar un dato del documento:', err);
      })
      .finally(() => {
        if (!cancelled) setImagesReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [token, budget?.idBudget]);

  const ready = itemsReady && imagesReady && staticReady && !templateLoading;

  const data = useMemo(
    () => buildBudgetData(budget, items, images, staticResponse),
    [budget, items, images, staticResponse]
  );

  const engineConfig = useMemo(
    () => ({
      logo: staticResponse?.logo ?? null,
      portraitLogo: staticResponse?.portraitLogo ?? null,
    }),
    [staticResponse]
  );

  const engineColors = staticResponse?.colors ?? undefined;

  // El `loading` de PDFDownloadLink solo dice si termino de ARMAR el PDF, no si
  // los datos llegaron. Con los fetches en vuelo el documento se arma vacio, se
  // genera rapidisimo y el boton queda habilitado: quien haga clic en esa
  // ventana se lleva un PDF sin los datos del tenant (y potencialmente sin
  // items ni imagenes). Por eso el link no se monta hasta tener todo.
  if (!ready) {
    return (
      <Button type="primary" disabled block>
        Cargando datos...
      </Button>
    );
  }

  return (
    <PDFDownloadLink
      document={
        <TemplateRenderer
          template={template}
          data={data}
          config={engineConfig}
          colors={engineColors}
        />
      }
      fileName={`NDR-${budget.idBudget}.pdf`}
    >
      {({ loading }) => (
        <Button type="primary" disabled={loading} block>
          {loading ? 'Generando documento...' : 'Descargar PDF'}
        </Button>
      )}
    </PDFDownloadLink>
  );
}
export default BudgetDowload;
